/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Bugsnag from '@bugsnag/js';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuIcon,
  MenuItem,
  MenuList,
  Spacer,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/core';
import isNil from 'lodash/isNil';
import React, { Fragment, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBriefcase } from 'react-icons/fa';
import { IoIosListBox, IoIosLogOut, IoMdChatbubbles, IoMdHelpCircle, IoMdNotifications } from 'react-icons/io';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import { ReactComponent as _IconCalendar } from '../../assets/img/icon-calendar.svg';
import { RemoteConfigKey } from '../../firebase/remoteConfig';
import { useStoreActions, useStoreState } from '../../models/hooks';
import { ProfilePicture } from '../../modules/common';
// import { JobPositionModalView } from '../../modules/jobPositionTopCandidate/JobPositionModalView';
import { ProfileView } from '../../modules/profile/ProfileView';
import { PATH_CALENDAR, PATH_DASHBOARD, PATH_HIRE_FORM, PATH_MAIN, PATH_POSITIONS } from '../../routes/constants';
import colors from '../../styles/colors';
import HeaderMenuWithPopover from '../components/HeaderMenuModal';
import useAccounts from '../hooks/useAccounts';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { headerGradientCSS, headerGradientCSSWhenSortAndSortIsOpen, menuItemCss, notificationCount } from './Header.styles';
import { HeaderMenuItem } from './HeaderMenuItem';
import { Logo } from './Logo';
import { NotificationPanel } from './notification/NotificationPanel';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useMeetContext } from '../../modules/meet/MeetContext';

const IconCalendar = memo(_IconCalendar);

type HeaderProps = {
  showMenu: boolean;
  showLanguageSwitcher?: boolean;
  showSearchSort?: boolean;
  showPageTitle?: string;
  // eslint-disable-next-line react/require-default-props
  setDarkModeTo?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Header = ({
  showMenu,
  showLanguageSwitcher,
  showSearchSort = true,
  showPageTitle = '',
  setDarkModeTo,
}: HeaderProps): JSX.Element => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmationOpen, onOpen: onConfirmationOpen, onClose: onConfirmationClose } = useDisclosure();

  const { room, disconnect } = useMeetContext();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDarkMode, setisDarkMode] = React.useState<boolean>(false);
  const cancelRef = React.useRef() as React.MutableRefObject<HTMLButtonElement>;

  const [showAnalytics, setShowAnalytics] = React.useState<boolean>(false);
  const [showPrototype, setShowPrototype] = React.useState<boolean>(false);
  const [openProfile, setOpenProfile] = React.useState<boolean>(false);
  const [showCalendar, setShowCalendar] = React.useState<boolean>(false);
  const [showDashboard, setShowDashboard] = React.useState<boolean>(false);
  const [newNotificationCount, setNewNotificationCount] = React.useState<number>(0);
  const setAccounts = useStoreActions((a) => a.app.setAccounts);
  const accounts = useStoreState((s) => s.app.accounts);

  const appUserId = useStoreState((s) => s.app.user?.id);
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);

  const { updateLocale } = useStoreActions((actions) => actions.user);

  const match = useRouteMatch();
  const { t, i18n } = useTranslation();

  const { currentUserProfile, initialized } = useCurrentUserProfile();
  const accountsData = useAccounts();
  const recruiterProfileImage = useStoreState((s) => s.app.recruiterProfileImage);

  const signOut = useStoreActions((actions) => actions.auth.signOut);

  const enableNotificationFeature: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_NOTIFICATION_FEATURE];
  });
  const enableUpdateProfileFeature: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_UPDATE_PROFILE_FEATURE_WEB_RECRUITER];
  });
  // eslint-disable-next-line
  const enableTopCandidatesOfPosition: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_TOP_CANDIDATES_OF_POSITION];
  });

  const isPositionManagementFeatureEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_POSITION_MANAGEMENT_FEATURE];
  });

  const isSupportDocumentationEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_SUPPORT_DOCUMENTATION];
  });
  // eslint-disable-next-line
  const isNewAnalyticEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_NEW_ANALYTIC_VIEW];
  });

  const superOwners = useStoreState((s) => s.app.accounts?.superOwners);

  const isCurrentPage = (path: string): boolean => match?.url?.includes(path);

  const location = useLocation();

  const updateLocaleHandler = () => {
    if (currentUserProfile) {
      updateLocale({
        userId: currentUserProfile.id,
        locale: i18n.language === 'fr' ? 'en' : 'fr',
      });
    }
    i18n
      .changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')
      .then(() => {})
      .catch((error) => {
        Bugsnag.notify(error);
      });
  };

  const unreadNotificationNumber = (num: number) => {
    setNewNotificationCount(num);
  };

  useEffect(() => {
    if (!accountsData) return;
    setAccounts(accountsData);
  }, [accountsData, setAccounts]);

  useEffect(() => {
    setDarkModeTo?.(isDarkMode);
  }, [isDarkMode, setDarkModeTo]);

  useEffect(() => {
    const isAnalyticsEnabled = accounts?.analyticsEnabled === true;
    const isPrototypingEnabled = accounts?.prototypeEnabled === true;
    const isCalendarEnabled = accounts?.calendarEnabled === true;
    const isDashboardEnabled = accounts?.configuration?.dashboardEnabled === true;
    setShowDashboard(isDashboardEnabled);
    setShowAnalytics(isAnalyticsEnabled);
    setShowPrototype(isPrototypingEnabled);
    setShowCalendar(isCalendarEnabled);
  }, [accounts]);

  useEffect(() => {
    i18n
      .changeLanguage(currentUserProfile?.locale)
      .then(() => {})
      .catch((error) => {
        Bugsnag.notify(error);
      });
  }, [currentUserProfile, i18n]);

  const isManagementOverviewEnabled = useStoreState((s) => s.app.accounts?.configuration?.managementOverviewEnabled);
  const isSuperAdmin = (): boolean => {
    if (!superOwners || !appUserId) {
      return false;
    }
    return superOwners?.includes(appUserId);
  };

  const isAdmin = !isNil(appOwnersId) && !isNil(appUserId) && appOwnersId?.includes(appUserId);

  const showManagementOverviewIcon = (isSuperAdmin() || isAdmin) && isManagementOverviewEnabled;

  return (
    <Fragment>
      <Flex
        data-testid="HeaderComponent"
        css={showSearchSort ? headerGradientCSSWhenSortAndSortIsOpen : headerGradientCSS}
        justify="space-between"
        align="center"
        p={4}
      >
        {showMenu ? (
          <Link to={PATH_MAIN}>
            <Logo />
          </Link>
        ) : (
          <Box as="span">
            <Logo />
          </Box>
        )}
        {showPageTitle && (
          <Text fontSize="20px" fontWeight={700} color="#ffffff">
            {showPageTitle}
          </Text>
        )}
        {showLanguageSwitcher && !showMenu && (
          <Flex align="center" justify="center" mx={2} my={1} position="relative">
            <LanguageSwitcher />
            {!isNil(appUserId) && (
              <Flex
                ml={2}
                justifyContent="center"
                alignItems="center"
                data-testid="Logout"
                onClick={() => onConfirmationOpen()}
                cursor="pointer"
                _hover={{ bg: colors.gray[200] }}
                py={2}
                px={{ base: 2, md: 4 }} // Narrower padding on mobile
                borderRadius="6px"
                // Optional: ensures it looks like a square icon on small screens
                minW={{ base: '40px', md: 'auto' }}
                mr={{ base: 6, md: 0 }}
              >
                <IoIosLogOut color={colors.white} />

                {/* Hide the text box on small screens, show as block/flex on medium+ */}
                <Box ml={{ base: 0, md: 2 }} display={{ base: 'none', md: 'block' }}>
                  <Text fontWeight={700} color={colors.white}>
                    {t('account.logout')}
                  </Text>
                </Box>
              </Flex>
            )}
          </Flex>
        )}
        {initialized && showMenu && (
          <Menu placement="bottom-end">
            <Flex justify="flex-end">
              {/* {!isNil(setDarkModeTo) && (
                <Flex alignItems="center" mr={4}>
                  <Text color={colors.white} fontWeight="bold" mr={2}>
                    Switch To Dark Mode
                  </Text>
                  <Switch
                    colorScheme="teal"
                    size="lg"
                    isChecked={isDarkMode}
                    onChange={(e) => setisDarkMode(e.target.checked)}
                    data-testid="setDarkMode"
                  />
                </Flex>
              )} */}

              {isSupportDocumentationEnabled && !isCurrentPage('/support') && (
                <Menu>
                  <MenuIcon marginTop={1} as={Box} marginRight={4}>
                    <Link to="/support">
                      <IoMdHelpCircle size={32} color="#fff" />
                    </Link>
                  </MenuIcon>
                </Menu>
              )}

              {showDashboard && (
                <Menu>
                  <Tooltip label={t('headerMenu:tooltip.dashboard')}>
                    <HeaderMenuItem
                      to={PATH_DASHBOARD}
                      icon={<IoIosListBox size={32} color="#fff" />}
                      tooltip={t('headerMenu:tooltip.dashboard')}
                      isActive={isCurrentPage(PATH_DASHBOARD)}
                    />
                  </Tooltip>
                </Menu>
              )}
              {!isCurrentPage('/candidates/') && (
                <Menu>
                  <Tooltip label={t('headerMenu:tooltip.messaging')}>
                    <HeaderMenuItem
                      to={PATH_MAIN}
                      icon={<IoMdChatbubbles size={32} color="#fff" />}
                      tooltip={t('headerMenu:tooltip.messaging')}
                      isActive={isCurrentPage(PATH_MAIN)}
                    />
                  </Tooltip>
                </Menu>
              )}
              {isPositionManagementFeatureEnabled && !isCurrentPage(PATH_HIRE_FORM) && (
                <Menu>
                  <Tooltip label={t('headerMenu:tooltip.jobs')}>
                    <HeaderMenuItem
                      to={PATH_POSITIONS}
                      icon={<FaBriefcase size={32} color="#fff" />}
                      tooltip={t('headerMenu:tooltip.jobs')}
                      isActive={isCurrentPage(PATH_POSITIONS)}
                    />
                  </Tooltip>
                </Menu>
              )}
              {/* 
              {enableTopCandidatesOfPosition && (
                <Menu>
                  <Tooltip label="Job Postings">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius={50}
                      paddingX={4}
                      paddingY={1}
                      transition="background-color 0.2s"
                      bg={isPositionMgmtForTopCandidateOpen ? 'rgba(255, 255, 255, 0.3)' : 'transparent'}
                      _hover={{ bg: 'rgba(255, 255, 255, 0.3)' }}
                      onClick={() => onPositionMgmtForTopCandidateOpen()}
                    >
                      <FaBriefcase size={32} color="#fff" />
                    </Box>
                  </Tooltip>
                </Menu>
              )} */}

              {showCalendar && (
                <Menu>
                  <HeaderMenuItem
                    to={PATH_CALENDAR}
                    icon={<IconCalendar width={30} height={30} />}
                    tooltip={t('headerMenu:tooltip.calendar')}
                    isActive={isCurrentPage(PATH_CALENDAR)}
                  />
                </Menu>
              )}

              <Menu>
                <HeaderMenuWithPopover
                  showPrototype={showPrototype}
                  showManagementOverviewIcon={showManagementOverviewIcon ?? false}
                  showAnalytics={showAnalytics}
                />
              </Menu>

              {enableNotificationFeature && (
                <Menu>
                  <HeaderMenuItem
                    icon={
                      <Box position="relative">
                        <Box css={notificationCount}>{newNotificationCount}</Box>
                        <IoMdNotifications size={32} color="#fff" />
                      </Box>
                    }
                    tooltip={t('headerMenu:tooltip.notification')}
                    onClick={onOpen}
                    asLink={false}
                    testId="NotificationMenuItem"
                    isActive={isOpen}
                    data-testid="NotificationMenuItem"
                  />
                </Menu>
              )}

              <Box zIndex={12}>
                <MenuButton as={Box} data-testid="ProfilePictureButton" trigger="hover" className="userProfilePic">
                  <Flex align="center">
                    <ProfilePicture
                      data-testid="UserProfilePicture"
                      name={currentUserProfile?.fullName}
                      style={{ backgroundColor: colors.blue.default, color: 'white' }}
                      src={recruiterProfileImage}
                      width="40px"
                      height="40px"
                    />
                  </Flex>
                </MenuButton>
                <MenuList
                  backgroundColor="#ffffff"
                  paddingLeft="10px"
                  paddingRight="10px"
                  css={menuItemCss}
                  w="350px"
                  maxW="350px"
                >
                  <Box paddingTop="10px" paddingBottom="10px">
                    <Flex align="center" overflow="hidden">
                      <ProfilePicture
                        name={currentUserProfile?.fullName}
                        style={{ backgroundColor: colors.blue.default, color: 'white' }}
                        src={recruiterProfileImage}
                        width="40px"
                        height="40px"
                        flexShrink={0}
                      />
                      <Box pl={2} color="#000" flex="1" minW="0" overflow="hidden">
                        <Heading
                          as="h4"
                          fontSize="sm"
                          marginTop={0}
                          marginBottom={0}
                          lineHeight={1.1}
                          overflow="hidden"
                          whiteSpace="nowrap"
                          textOverflow="ellipsis"
                        >
                          {currentUserProfile?.fullName}
                        </Heading>
                        <Tooltip
                          label={currentUserProfile?.email}
                          placement="bottom-start"
                          bg="white"
                          color="#394951"
                          fontSize="xs"
                          borderRadius="md"
                        >
                          <Text
                            fontSize="xs"
                            color="#a6a6a6"
                            overflow="hidden"
                            whiteSpace="nowrap"
                            textOverflow="ellipsis"
                            cursor="default"
                          >
                            {currentUserProfile?.email}
                          </Text>
                        </Tooltip>
                      </Box>
                    </Flex>
                  </Box>
                  {enableUpdateProfileFeature && (
                    <MenuItem onClick={() => setOpenProfile(true)} className="menuItem">
                      {t('account.myProfile')}
                    </MenuItem>
                  )}
                  {!isNil(appOwnersId) &&
                    !isNil(appUserId) &&
                    appOwnersId?.includes(appUserId) &&
                    (location.pathname === '/administration' ? (
                      <Link to="/messaging" style={{ textDecoration: 'none' }}>
                        <MenuItem className="menuItem">{t('account.recruiter')}</MenuItem>
                      </Link>
                    ) : (
                      <Link to="/administration" style={{ textDecoration: 'none' }}>
                        <MenuItem className="menuItem">{t('account.administration')}</MenuItem>
                      </Link>
                    ))}
                  <MenuItem data-testid="LanguageChanger" onClick={updateLocaleHandler} className="menuItem">
                    {t('account.switchLang')}
                  </MenuItem>
                  <MenuItem data-testid="Logout" onClick={() => onConfirmationOpen()} className="menuItem">
                    <Flex alignItems="center" justifyContent="space-between" width="100%">
                      <Box>
                        <Text>{t('account.logout')}</Text>
                      </Box>
                      <Spacer />
                      <Box>
                        <IoIosLogOut color="#000" />
                      </Box>
                    </Flex>
                  </MenuItem>
                </MenuList>
              </Box>
            </Flex>
          </Menu>
        )}
        {isOpen && enableNotificationFeature && (
          <NotificationPanel isOpen={isOpen} onClose={onClose} unreadNotificationNumber={unreadNotificationNumber} />
        )}
        {openProfile && (
          <ProfileView open={openProfile} onCloseProfile={() => setOpenProfile(false)} picture={recruiterProfileImage} />
        )}
        {/* {isPositionMgmtForTopCandidateOpen && (
          <JobPositionModalView isOpen={isPositionMgmtForTopCandidateOpen} onClose={onPositionMgmtForTopCandidateClose} />
        )} */}
        {isConfirmationOpen && (
          <AlertDialog isOpen={isConfirmationOpen} leastDestructiveRef={cancelRef} onClose={onConfirmationClose}>
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  {t('logoutConfirmation.message')}
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <Button ref={cancelRef} colorScheme="blue" onClick={onConfirmationClose}>
                    {t('logoutConfirmation.cancel')}
                  </Button>
                  <Button
                    data-testid="confirmSignOutButton"
                    colorScheme="red"
                    onClick={() => {
                      if (room) {
                        disconnect();
                      }
                      signOut().finally(() => onConfirmationClose());
                    }}
                    ml={3}
                  >
                    {t('logoutConfirmation.logout')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )}
      </Flex>
    </Fragment>
  );
};

Header.defaultProps = {
  showSearchSort: true,
  showPageTitle: '',
  showLanguageSwitcher: false,
};
